/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { isIOS, isMacOS } from 'sarsaparilla';

import { getSuggestionLabel } from './QuickSearch.transforms';
import type { SearchSuggestionGroup, SearchSuggestionItem } from './QuickSearch.types';

/**
 * VoiceOver reads an `aria-activedescendant` option without saying which group it
 * sits in, so Apple platforms get group changes announced through a live region.
 * Other screen readers announce groups themselves and would hear it twice.
 */
export function shouldAnnounceHighlights(): boolean {
    return isMacOS() || isIOS();
}

/** The group that renders `item`, if any. */
export function findSuggestionGroup(
    groups: SearchSuggestionGroup[],
    item: SearchSuggestionItem
): SearchSuggestionGroup | undefined {
    return groups.find((group) => group.items.includes(item));
}

type GroupEntryAnnouncementOptions = {
    item: SearchSuggestionItem;
    group: SearchSuggestionGroup | undefined;
    isGroupChange: boolean;
};

/**
 * What to announce when keyboard navigation enters a labelled group: its name and
 * size, matching react-aria's combobox, then the row as shown. The assertive announcement cuts
 * off VoiceOver's own reading of the option, so the row has to be repeated here.
 * Within a group VoiceOver's reading is enough, so nothing is announced.
 */
export function getGroupEntryAnnouncement({
    item,
    group,
    isGroupChange,
}: GroupEntryAnnouncementOptions): string | undefined {
    if (!isGroupChange || !group?.label) {
        return undefined;
    }

    const count = group.items.length;
    const options = count === 1 ? 'option' : 'options';

    const row = item.description
        ? `${getSuggestionLabel(item)}, ${item.description}`
        : getSuggestionLabel(item);

    return `Entered group ${group.label}, with ${count} ${options}. ${row}`;
}
